import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { commitUndo } from '../../app/discovery';
import { log } from '../../app/logger';
import { config } from '../../config';
import { msUntilUndoCloses, pickUndoTarget } from '../../domain/undo';
import { undoCountdown } from './bandB';
import type { UndoRowB } from './bandB';
import type { MatchRow } from '../../types';

/**
 * B-11 — the **undo pill** (design spec §2.4; scenario 9): LIVE-6's affordance in the thumb
 * zone, directly above the Flag button, on **every** in-match surface — including the break hub,
 * where no row renders (the `predicate-without-surface` class of defect this chrome placement
 * closes: an increment at 44:58 survives the Half whistle for its full window, and the pill is
 * the only thing left to undo it from).
 *
 * Every decision is the engine's, per the reuse contract:
 *
 * - **which** hypothesis (and whether any) — `pickUndoTarget` over committed rows: the single
 *   newest open window, re-evaluated on every render. Repeat-undo's fall-through needs no queue
 *   (undoing the newest makes the next-newest the answer) and the pill NAMES its target, which
 *   is what keeps LIVE-6's "never deletes an increment on a hypothesis it does not name" a fact
 *   the user can check.
 * - **when to stop showing it** — `msUntilUndoCloses`, armed as A's one-shot re-eval (NFR-5:
 *   no window open ⇒ no timer held; never an interval, never truth — the predicate over
 *   committed data is the truth, and the handler re-checks it in-transaction regardless).
 * - **the dispatch** — the same `commitUndo` facade both of A's undo entries ride
 *   (`UndoIncrement`; the command carries the hypothesis id and no observation id — the target
 *   increment is picked in-transaction). A second command would be a parallel path; this is
 *   deliberately not one.
 *
 * What is B's own is rendering: the **visible countdown** — a track inside the pill animating
 * `scaleX` from the window fraction left at mount down to zero over exactly the remaining
 * milliseconds (`undoCountdown`, computed ONCE per target-window and keyed so a re-anchoring
 * increment restarts it; no per-second tick, no polling — the same "the visual and the timer
 * cannot disagree" discipline as the transition hold's fill). The ack flash lands on the pill
 * inside `onCommitted` — the bus's native-`complete` seam — never optimistically.
 */

const MODULE = 'ui-b/band';

export interface UndoPillBProps {
  readonly match: MatchRow;
  readonly rows: readonly UndoRowB[];
  /** An undo committed — the host re-reads (`lastIncrementTs` rewrites to the next-newest
   *  increment, which no surface can recompute without the ledger; A re-reads for the same
   *  reason). */
  readonly onUndone: () => void;
  /** A rejection or failure, surfaced in the band's one notice slot (never swallowed, NFR-8). */
  readonly onNotice: (message: string) => void;
}

export function UndoPillB({ match, rows, onUndone, onNotice }: UndoPillBProps) {
  /**
   * A's one-shot re-eval (UndoStrip shape): re-render once, strictly after the earliest open
   * window closes, so the pill disappears promptly without polling. Nothing is lost if it never
   * fires (a backgrounded page re-renders on return and the predicate re-evaluates from
   * committed data).
   */
  const [, setTick] = useState(0);
  const timer = useRef<number | undefined>(undefined);
  /** Synchronous double-tap guard — `busy` state would only disable after the next commit. */
  const busy = useRef(false);

  const target = pickUndoTarget(rows, match.state, Date.now());

  useEffect(() => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
    const closesIn = msUntilUndoCloses(rows, match.state, Date.now());
    // NFR-5's property at its only decision point: nothing open, nothing armed.
    if (closesIn === null) return;
    // `+ 1` so the callback runs strictly after the boundary (`undoOpen` is `now − last <
    // window`, so firing exactly on it could still read as open and re-arm at zero).
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      setTick((value) => value + 1);
    }, closesIn + 1);
    return () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current);
      timer.current = undefined;
    };
  });

  /**
   * Computed once per (target, window) and cached: the animation runs off these two numbers,
   * and recomputing them on unrelated re-renders would feed a RUNNING animation a new
   * duration mid-flight (a visual jump). The `key` below remounts the element — and restarts
   * the animation from the fresh fraction — exactly when the window re-anchors or the target
   * falls through.
   */
  const targetId = target?.id ?? null;
  const targetLast = target?.lastIncrementTs ?? null;
  const countdown = useMemo(
    () => undoCountdown(targetLast, Date.now()),
    // Keyed on the window's identity, deliberately not on `Date.now()`: one computation per
    // (target, window), not per render. `targetId` is in the list because a fall-through to a
    // different hypothesis with the same millisecond timestamp is a new window.
    [targetId, targetLast],
  );

  if (target === null || countdown === null) return null;

  const trackStyle = {
    animationDuration: `${String(Math.round(countdown.remainingMs))}ms`,
    '--undo-from': String(countdown.fraction),
  } as CSSProperties;

  const undoOne = (element: HTMLElement): void => {
    if (busy.current) return;
    busy.current = true;
    void commitUndo(match.id, target.id, () => {
      // The ONLY acknowledgment (SES-2): applied inside the native IndexedDB `complete`
      // listener, on the element captured at the tap. Remove → reflow → set so a rapid
      // fall-through undo restarts the flash instead of showing nothing.
      delete element.dataset.ack;
      void element.offsetWidth;
      element.dataset.ack = 'true';
      window.setTimeout(() => {
        delete element.dataset.ack;
      }, config.ACK_PERSIST_MIN_MS);
    }).then(
      (result) => {
        busy.current = false;
        if (!result.ok) {
          // A closed-window race is a normal outcome the in-transaction re-check owns; the
          // message is the domain's own sentence, rendered as-is.
          onNotice(result.message);
          return;
        }
        onUndone();
      },
      (cause: unknown) => {
        busy.current = false;
        const message = cause instanceof Error ? cause.message : String(cause);
        onNotice(message);
        log.error(MODULE, 'an undo failed and was rolled back', { message });
      },
    );
  };

  return (
    <button
      type="button"
      // Remount per window so the countdown animation restarts exactly when the window does.
      key={`${target.id}:${String(target.lastIncrementTs ?? 0)}`}
      className="bandb-undo"
      data-testid="bandb-undo"
      data-hid={target.id}
      onClick={(event) => {
        undoOne(event.currentTarget);
      }}
    >
      <span className="bandb-undo__minus" aria-hidden="true">
        −1
      </span>
      {/* The hypothesis is NAMED — the thing the pill will undo is the thing it says. */}
      <span className="bandb-undo__text">{target.text}</span>
      <span className="bandb-undo__track" aria-hidden="true" style={trackStyle} />
    </button>
  );
}
